/**
 * Component: Show password
 * -----------------------------------------------------------------------------
 * Show/hide password functionality.
 *
 */
import classes from '~/config/classes'

/**
 * DOM selectors.
 */
const selectors = {
  input: 'input[type="password"]',
  button: '[js-show-password="button"]',
}

/**
 * Export show password.
 */
export default () => {

  /**
   * DOM node selectors.
   */
  const nodeSelectors = {
    inputs: [...document.querySelectorAll(selectors.input)],
  }

  /**
   * Initialise component.
   */
  function init() {
    if (!nodeSelectors.inputs.length) {
      return
    }

    buildShowPasswordElements()
  }

  /**
   * Add show/hide markup to password inputs and set event listeners.
   */
  function buildShowPasswordElements() {
    nodeSelectors.inputs.forEach((input) => {
      if (input.classList.contains(classes.active)) {
        return
      }

      updateInput(input)
    })

    setEventListeners()
  }

  /**
   * Update markup for password input.
   * - Wraps input in a div.
   * - Updates classes of input.
   * - Adds button and accessibility element into the wrapper div.
   * @param {HTMLElement} input - Password input element.
   */
  function updateInput(input) {
    const wrapperDiv = document.createElement('div')
    wrapperDiv.classList.add('show-password__input-wrapper')
    input.parentNode.insertBefore(wrapperDiv, input)
    wrapperDiv.append(input)
    input.classList.add('show-password__input', classes.active)

    const additionalElementsHtml = buildInternalElements(input)
    wrapperDiv.insertAdjacentHTML('beforeend', additionalElementsHtml)
  }

  /**
   * Build markup for toggle button and accessibility element.
   * @param {HTMLElement} input - Password input element
   * @returns {String}
   */
  function buildInternalElements(input) {
    return `
      <label
        class="form-group__label text-body-2xs-regular"
        for="password"
      >
        ${input.id === 'password-confirmation'
    ? cnvs.strings.customers.fields.password_confirmation
    : cnvs.strings.customers.fields.password}
      </label>
    `
  }

  /**
   * Set event listeners.
   */
  function setEventListeners() {
    nodeSelectors.buttons = [...document.querySelectorAll(selectors.button)]

    nodeSelectors.buttons.forEach((button) => {
      const form = button.closest('form')
      button.addEventListener('click', togglePassword)

      if (!form) {
        return
      }

      form.addEventListener('submit', hideAllPasswords)
    })
  }

  /**
   * Toggle showing and hiding password in field.
   * @param {Object} event - Click event.
   */
  function togglePassword(event) {
    const { currentTarget } = event
    const input = currentTarget.previousElementSibling
    const screenReader = currentTarget.nextElementSibling

    if (input.type === 'password') {
      showPassword({ button: currentTarget, input, screenReader })
      return
    }

    hidePassword({ button: currentTarget, input, screenReader })
  }

  /**
   * Show password in field.
   * @param {HTMLElement} button - Show/hide button.
   * @param {HTMLElement} input - Password field.
   * @param {HTMLElement} screenReader - Accessibility element.
   */
  function showPassword({ button, input, screenReader }) {
    button.innerText = cnvs.strings.customers.password.hide
    button.setAttribute('aria-label', cnvs.strings.accessibility.password.hide)
    input.type = 'text'
    screenReader.innerText = cnvs.strings.accessibility.password.shown_message
  }

  /**
   * Hide password in field.
   * @param {HTMLElement} button - Show/hide button.
   * @param {HTMLElement} input - Password field.
   * @param {HTMLElement} screenReader - Accessibility element.
   */
  function hidePassword({ button, input, screenReader }) {
    button.innerText = cnvs.strings.customers.password.show
    button.setAttribute('aria-label', cnvs.strings.accessibility.password.show)
    input.type = 'password'

    if (!screenReader) {
      return
    }

    screenReader.innerText = cnvs.strings.accessibility.password.hidden_message
  }

  /**
   * Set all password inputs to 'password' type on form submit.
   * - Doesn't pass `screenReader` element to prevent triggering multiple
   *   `aria-live` regions at once.
   */
  function hideAllPasswords() {
    nodeSelectors.buttons.forEach((button) => {
      hidePassword({ button, input: button.previousElementSibling })
    })
  }

  return Object.freeze({
    init,
  })
}
